import PropTypes from 'prop-types';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Stack, Typography } from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInUp } from '../../../components/animate';
import { SectionHeader, SectionShell } from '../common';

// ----------------------------------------------------------------------

// These are programme attributes the product can substantiate from its
// course offering. Numerical totals belong here only after they come from a
// maintained API or CMS source with an owner and a last-verified date.
const FEATURELIST = [
  {
    name: 'Exam-focused learning',
    subtitle: 'Preparation aligned to DHA, MOH, HAAD and Prometric exam pathways.',
    icon: 'line-md:clipboard-check-twotone',
    color: '#DF3E30',
  },
  {
    name: 'Mentor guidance',
    subtitle: 'Structured support from healthcare exam preparation mentors.',
    icon: 'line-md:account',
    color: '#1877F2',
  },
  {
    name: 'Flexible study access',
    subtitle: 'Online learning resources designed to fit around work and study.',
    icon: 'line-md:play-twotone',
    color: '#1C9CEA',
  },
  {
    name: 'Practice-led preparation',
    subtitle: 'Exam-style questions and revision material for focused practice.',
    icon: 'line-md:question-circle',
    color: '#FFC107',
  },
];

FeatureItem.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
};

function FeatureItem({ item, index }) {
  return (
    <Stack
      spacing={{ xs: 1.25, md: 1.5 }}
      sx={{
        height: '100%',
        textAlign: 'center',
        alignItems: 'center',
        px: { xs: 1, md: 2.5 },
        py: { xs: 1, md: 0 },
        borderLeft: {
          md: index === 0 ? 'none' : `1px solid ${alpha('#FFFFFF', 0.12)}`,
        },
        transition: 'transform 0.3s ease',
        '@media (hover: hover)': {
          '&:hover': { transform: 'translateY(-4px)' },
          '&:hover .feature-icon': {
            transform: 'scale(1.1)',
            boxShadow: `0 0 0 6px ${alpha(item.color, 0.14)}`,
          },
        },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          '&:hover': { transform: 'none' },
        },
      }}
    >
      <Box
        className="feature-icon"
        sx={{
          width: { xs: 48, sm: 54, md: 58 },
          height: { xs: 48, sm: 54, md: 58 },
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          color: item.color,
          bgcolor: alpha(item.color, 0.16),
          border: `1px solid ${alpha(item.color, 0.3)}`,
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        }}
      >
        <Iconify icon={item.icon} width={28} />
      </Box>

      <Typography
        variant="subtitle1"
        sx={{ color: 'common.white', fontWeight: 700, fontSize: { xs: 14, md: 16 }, lineHeight: 1.4 }}
      >
        {item.name}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: (theme) => alpha(theme.palette.common.white, 0.68),
          maxWidth: 240,
          lineHeight: 1.65,
        }}
      >
        {item.subtitle}
      </Typography>
    </Stack>
  );
}

// ----------------------------------------------------------------------

export default function FeatureHWidget() {
  return (
    <SectionShell>
      <SectionHeader
        eyebrow="What you can expect"
        title="Built for focused exam preparation"
        description="Practical learning support designed to help healthcare professionals prepare with clarity and confidence."
      />

      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 4, md: 5 },
          color: 'common.white',
          bgcolor: 'grey.900',
          border: `1px solid ${alpha('#FFFFFF', 0.08)}`,
          backgroundImage: (theme) =>
            `radial-gradient(120% 140% at 0% 0%, ${alpha(theme.palette.primary.main, 0.4)} 0%, ${alpha(
              theme.palette.grey[900],
              0
            )} 46%), radial-gradient(120% 140% at 100% 100%, ${alpha('#1877F2', 0.32)} 0%, ${alpha(
              theme.palette.grey[900],
              0
            )} 46%)`,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            columnGap: { xs: 2, md: 0 },
            rowGap: { xs: 3.5, md: 0 },
          }}
        >
          {FEATURELIST.map((item, index) => (
            <MotionInView
              key={item.name}
              variants={varFadeInUp}
              transition={{ delay: index * 0.1 }}
              sx={{ height: '100%' }}
            >
              <FeatureItem item={item} index={index} />
            </MotionInView>
          ))}
        </Box>
      </Box>
    </SectionShell>
  );
}
